import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Flag, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { RED_FLAG_REASONS, type RedFlagReasonValue } from "@/components/ui/red-flag-badge";
import type { Creator } from "@shared/schema";

interface RedFlagModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  creatorId: string;
  creatorName: string;
  isCurrentlyFlagged?: boolean;
  currentReason?: string | null;
  currentNote?: string | null;
}

export function RedFlagModal({
  open,
  onOpenChange,
  creatorId,
  creatorName,
  isCurrentlyFlagged = false,
  currentReason,
  currentNote,
}: RedFlagModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<string>(currentReason || "");
  const [note, setNote] = useState(currentNote || "");

  const flagMutation = useMutation({
    mutationFn: async () => {
      console.log('[RED FLAG] Starting mutation - creatorId:', creatorId, 'reason:', reason, 'note:', note);
      const response = await fetch(`/api/creators/${creatorId}/redflag`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, note: note.trim() || null }),
      });

      if (!response.ok) {
        throw new Error('Failed to flag creator');
      }

      const data = await response.json();
      console.log('[RED FLAG] Mutation response data:', data);
      return data;
    },
    onSuccess: async (updatedCreator) => {
      console.log('[RED FLAG] onSuccess - updated creator from API:', updatedCreator);

      // Mettre à jour directement avec les données du serveur
      queryClient.setQueryData([`/api/creators/${creatorId}`], updatedCreator);
      console.log('[RED FLAG] Cache updated with server data');

      // Mettre à jour IMMÉDIATEMENT la liste (évite la race condition avec le refetch async)
      queryClient.setQueryData(['/api/creators'], (oldData: Creator[] | undefined) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map(c => c.id === creatorId ? { ...c, ...updatedCreator } : c);
      });
      console.log('[RED FLAG] List cache updated immediately');

      // Invalider aussi pour forcer un refetch frais en background
      queryClient.invalidateQueries({ queryKey: [`/api/creators/${creatorId}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/creators'] });
      console.log('[RED FLAG] Queries invalidated - refetch triggered');

      toast({
        title: "✅ Créateur signalé",
        description: `${creatorName} a été marqué avec un drapeau rouge.`,
        duration: 5000,
      });

      // Attendre un peu pour que le toast soit visible
      setTimeout(() => {
        onOpenChange(false);
      }, 300);
    },
    onError: (error) => {
      console.error('[RED FLAG] onError called:', error);
      toast({
        title: "❌ Erreur",
        description: "Impossible de signaler ce créateur.",
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  const unflagMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/creators/${creatorId}/redflag`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error('Failed to unflag creator');
      }

      const data = await response.json();
      console.log('[RED FLAG] Unflag response data:', data);
      return data;
    },
    onSuccess: async (updatedCreator) => {
      console.log('[RED FLAG] Unflag success - updated creator:', updatedCreator);

      // Mettre à jour directement avec les données du serveur
      queryClient.setQueryData([`/api/creators/${creatorId}`], updatedCreator);
      console.log('[RED FLAG] Cache updated with server data (unflagged)');

      // Mettre à jour IMMÉDIATEMENT la liste (évite la race condition avec le refetch async)
      queryClient.setQueryData(['/api/creators'], (oldData: Creator[] | undefined) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map(c => c.id === creatorId ? { ...c, ...updatedCreator } : c);
      });
      console.log('[RED FLAG] List cache updated immediately (unflag)');

      // Invalider aussi pour forcer un refetch frais en background
      queryClient.invalidateQueries({ queryKey: [`/api/creators/${creatorId}`] });
      queryClient.invalidateQueries({ queryKey: ['/api/creators'] });
      console.log('[RED FLAG] Queries invalidated - refetch triggered (unflag)');

      toast({
        title: "✅ Signal retiré",
        description: `Le drapeau rouge de ${creatorName} a été retiré.`,
        duration: 5000,
      });

      setTimeout(() => {
        onOpenChange(false);
      }, 300);
    },
    onError: () => {
      toast({
        title: "❌ Erreur",
        description: "Impossible de retirer le signal.",
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  const handleSubmit = () => {
    if (!reason) {
      toast({
        title: "Raison requise",
        description: "Veuillez sélectionner une raison.",
        variant: "destructive",
      });
      return;
    }
    flagMutation.mutate();
  };

  const handleRemoveFlag = () => {
    unflagMutation.mutate();
  };

  const isPending = flagMutation.isPending || unflagMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="modal-red-flag">
        <DialogHeader className="pb-4 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-destructive/10">
              <Flag className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle className="text-destructive">
                {isCurrentlyFlagged ? "Modifier le signalement" : "Signaler un créateur"}
              </DialogTitle>
              <DialogDescription className="mt-0.5">
                {isCurrentlyFlagged
                  ? `Modifiez le signalement de ${creatorName} ou retirez-le.`
                  : `Signaler ${creatorName} comme créateur à éviter.`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="reason">Raison du signalement *</Label>
            <Select
              value={reason}
              onValueChange={setReason}
            >
              <SelectTrigger id="reason" data-testid="select-redflag-reason">
                <SelectValue placeholder="Sélectionnez une raison" />
              </SelectTrigger>
              <SelectContent>
                {RED_FLAG_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Note (optionnel)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ajoutez des détails si nécessaire..."
              className="resize-none"
              rows={3}
              data-testid="textarea-redflag-note"
            />
          </div>

          {isCurrentlyFlagged && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-muted">
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Ce créateur est actuellement signalé.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row pt-4 border-t border-border/50">
          {isCurrentlyFlagged && (
            <Button
              type="button"
              variant="outline"
              onClick={handleRemoveFlag}
              disabled={isPending}
              data-testid="button-remove-redflag"
            >
              Retirer le signal
            </Button>
          )}
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={isPending || !reason}
            data-testid="button-confirm-redflag"
          >
            <Flag className="h-4 w-4 mr-2" />
            {isCurrentlyFlagged ? "Mettre à jour" : "Signaler"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
